import { Delete, Edit } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import { Box, Button } from "@mui/material";
import { GridCellParams, GridColDef } from "@mui/x-data-grid";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import { useDeleteAdditionalInformationMutation, Contact } from "@repo/server-api";
import { EmployeeWorkingConditionInformationType } from "@repo/server-api/types";
import { useNotification } from "@repo/shared/components";
import dayjs from "dayjs";
import { useTranslation } from "next-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";

import { emptyContact, deepEqual, setContactValues } from "@repo/legacy/components/helper/Index";

import {
	useCreateEmployeeWorkingConditionInformation,
	useUpdateEmployeeWorkingConditionInformation,
} from "../../hooks/ContactMutationHooks";
import ConformationDialog from "../dialogs/Conformation";
import DialogWorkingCondition from "../dialogs/DialogWorkingConditiion";

const WorkingConditionSection = (
	props: Readonly<{
		contact?: Contact | null;
		setContact?: (value: Contact | null) => void;
		errors?: Record<string, boolean | object | string>;
		setErrors?: (value: Record<string, boolean | object | string>) => void;
	}>,
) => {
	const [contact, setContact] = useState<Contact | null>(null);
	const [informationId, setInformationId] = useState<string>("");
	const [openDialog, setOpenDialog] = useState<boolean>(false);
	const [workingCondition, setWorkingCondition] =
		useState<EmployeeWorkingConditionInformationType | null>(null);
	const [workingConditionList, setWorkingConditionList] = useState<
		EmployeeWorkingConditionInformationType[]
	>([]);

	const { showNotification } = useNotification();

	const [refresh, setRefresh] = useState<boolean>(false);

	const [updateWorkingCondition] = useUpdateEmployeeWorkingConditionInformation();
	const [createWorkingCondition] = useCreateEmployeeWorkingConditionInformation();

	const [deleteWorkingConditioin] = useDeleteAdditionalInformationMutation({
		onCompleted(data) {
			if (data.deleteAdditionalInformation.boolean) {
				const newContact = {
					...setContactValues(contact),
				};
				newContact.additionalInformation = newContact.additionalInformation?.filter(
					(i) => i.id !== informationId,
				);
				setContact(newContact);
				props.setContact?.(newContact);
				showNotification(t("working-condition-deleted"), "success");
			}
		},
		onError(error) {
			showNotification(error.message, "error");
		},
	});

	const getAllWorkingConditions = useCallback(() => {
		if (contact?.additionalInformation) {
			const workingConditions = contact.additionalInformation.filter(
				(info): info is EmployeeWorkingConditionInformationType =>
					info.__typename === "EmployeeWorkingConditionInformation",
			);
			setWorkingConditionList(workingConditions);
		} else {
			setWorkingConditionList([]);
		}
	}, [contact]);

	useEffect(() => {
		getAllWorkingConditions();
	}, [contact, workingCondition, getAllWorkingConditions]);

	useEffect(() => {
		if (props.contact && !deepEqual(contact ?? emptyContact, props.contact)) {
			setContact(props.contact);
		}
	}, [props.contact, contact]);

	const { t, i18n } = useTranslation();

	const columns: GridColDef<EmployeeWorkingConditionInformationType>[] = useMemo(
		() => [
			{
				field: "startDate",
				headerName: t("from"),
				width: 150,
				renderCell: (params: GridCellParams) => {
					return i18n.language === "en"
						? dayjs(params.row.from).format("MM/DD/YYYY")
						: dayjs(params.row.from).format("DD.MM.YYYY");
				},
			},
			{
				field: "endDate",
				headerName: t("to"),
				width: 150,
				renderCell: (params: GridCellParams) => {
					return i18n.language === "en"
						? dayjs(params.row.to).format("MM/DD/YYYY")
						: dayjs(params.row.to).format("DD.MM.YYYY");
				},
			},
			{
				field: "workingTimeModel",
				headerName: t("work-time-model"),
				width: 200,
				renderCell: (params: GridCellParams) => {
					return params.row.workingTimeModel?.name ?? "";
				},
			},
			{
				field: "federalLocation",
				headerName: t("federal-location"),
				width: 200,
				flex: 1,
				renderCell: (params: GridCellParams) => {
					return params.row.federalLocation ?? "";
				},
			},
			{
				field: "actions",
				headerName: t("actions"),
				sortable: false,
				width: 150,
				renderCell: (params: GridCellParams) => {
					const [openDeleteConformation, setOpenDeleteConformation] = useState<boolean>(false);

					const handleYes = (id: string) => {
						setInformationId(id);
						deleteWorkingConditioin({
							variables: {
								contactId: props.contact?.id ?? "",
								informationId: id,
							},
						});
						setOpenDeleteConformation(false);
					};

					const openDelete = () => {
						setOpenDeleteConformation(true);
					};

					const openEdit = (workingCondition: EmployeeWorkingConditionInformationType | null) => {
						setWorkingCondition(workingCondition);
						setOpenDialog(true);
					};

					return (
						<div>
							<Button data-testid="edit-button" onClick={() => openEdit(params.row)}>
								<Edit />
							</Button>
							<Button data-testid="delete-button" onClick={() => openDelete()}>
								<Delete />
							</Button>
							<ConformationDialog
								text={t("workingConditions.deleteConformation")}
								setOpenDeleteConformation={setOpenDeleteConformation}
								openPopup={openDeleteConformation}
								handleYes={() => handleYes(params.row.id.toString())}
							/>
						</div>
					);
				},
			},
		],
		[t, i18n.language, deleteWorkingConditioin, props.contact?.id],
	);

	useEffect(() => {
		if (refresh && workingCondition) {
			if (!workingCondition.id) {
				createWorkingCondition(contact?.id ?? "", workingCondition)
					.then((res) => {
						if (res.createEmployeeWorkingCondition.employeeWorkingConditionInformation.id) {
							const newWorkingCondition =
								res.createEmployeeWorkingCondition.employeeWorkingConditionInformation;
							setWorkingCondition(newWorkingCondition);
							const newContact = {
								...setContactValues(contact),
							};
							const isUpdated = newContact.additionalInformation?.some(
								(i) => i.id === newWorkingCondition.id,
							);
							if (!isUpdated) {
								newContact.additionalInformation = newContact.additionalInformation ?? [];
								newContact.additionalInformation.push(newWorkingCondition);
							} else {
								newContact.additionalInformation = newContact.additionalInformation?.map((i) =>
									i.id === newWorkingCondition.id ? newWorkingCondition : i,
								);
							}
							setContact(newContact);
							props.setContact?.(newContact);
							getAllWorkingConditions();
							showNotification(t("workingConditions.created"), "success");
						}
						if (res.createEmployeeWorkingCondition.errors) {
							const errorMessage = res.createEmployeeWorkingCondition.errors
								.map((error: { message: string }) => error.message)
								.join(", ");
							showNotification(errorMessage, "error");
						}
					})
					.catch((error) => {
						showNotification(error.message, "error");
					});
				setRefresh(false);
			} else if (workingCondition && workingCondition.id) {
				updateWorkingCondition(contact?.id ?? "", workingCondition)
					.then((res) => {
						if (res.updateEmployeeWorkingCondition.employeeWorkingConditionInformation.id) {
							const newWorkingCondition =
								res.updateEmployeeWorkingCondition.employeeWorkingConditionInformation;
							setWorkingCondition(newWorkingCondition);
							const newContact = {
								...setContactValues(contact),
							};
							const isUpdated = newContact.additionalInformation?.some(
								(i) => i.id === newWorkingCondition.id,
							);
							if (!isUpdated) {
								newContact.additionalInformation = newContact.additionalInformation ?? [];
								newContact.additionalInformation.push(newWorkingCondition);
							} else {
								newContact.additionalInformation = newContact.additionalInformation?.map((i) =>
									i.id === newWorkingCondition.id ? newWorkingCondition : i,
								);
							}
							setContact(newContact);
							props.setContact?.(newContact);
							getAllWorkingConditions();
							showNotification(t("workingConditions.updated"), "success");
						}
						if (res.updateEmployeeWorkingCondition.errors) {
							const errorMessage = res.updateEmployeeWorkingCondition.errors
								.map((error: { message: string }) => error.message)
								.join(", ");
							showNotification(errorMessage, "error");
						}
					})
					.catch((error) => {
						showNotification(error.message, "error");
					});
				setRefresh(false);
			}
		}
	}, [
		workingCondition,
		refresh,
		contact,
		props,
		createWorkingCondition,
		showNotification,
		t,
		updateWorkingCondition,
		getAllWorkingConditions,
	]);

	const handleWrokingConditiion = (info: EmployeeWorkingConditionInformationType) => {
		setWorkingCondition(info);
		setRefresh(true);
	};

	return (
		<Box sx={{ width: "100%" }}>
			<Box className="clear">
				<Button
					variant="contained"
					className="greenButton"
					onClick={() => {
						setWorkingCondition(null);
						setOpenDialog(true);
					}}>
					{t("add-new-working-condition")} <AddIcon />
				</Button>
			</Box>
			<DialogWorkingCondition
				workingCondition={workingCondition}
				setWorkingCondition={setWorkingCondition}
				handleSubmit={handleWrokingConditiion}
				setOpen={setOpenDialog}
				open={openDialog}
				contact={contact}
				setContact={setContact}
			/>
			{workingConditionList.length ? (
				<Box sx={{ height: "100%", width: "100%", minHeight: "500px" }}>
					<DataGridPremium
						getRowId={(row: EmployeeWorkingConditionInformationType) =>
							row ? row.id + row.key : ""
						}
						rows={workingConditionList ? workingConditionList : []}
						columns={columns as GridCellParams[]}
						disableRowSelectionOnClick
						className="table"
						hideFooter={true}
					/>
				</Box>
			) : (
				""
			)}
		</Box>
	);
};

WorkingConditionSection.displayName = "WorkingConditionSection";
export default WorkingConditionSection;
